<template>
  <div class="Pages w-100 h-100 py-5 mh-100">

    <div class="Pages__Controls d-flex justify-content-between align-items-bottom">
      <div class="d-flex align-items-center m-3">
        <strong class="mr-2">Weergave:</strong>
        <InlineToggle 
          :options="[{label: 'Basis', value: 'basic'}, {label: 'Detail', value: 'detailed'}]" 
          @selected="handleSelected" />
      </div>
      <b-button variant="cta" class="PrintBtn m-3" @click="handlePrint" data-html2canvas-ignore>
        Opslaan als PDF
      </b-button>
    </div>

    <Page v-if="hasFactsheetData" :landscape="true" :marginless="true" class="PageOne" ref="factsheet">

      <h1 class="PageOne__Header">
        Factsheet {{ municipalityLabel }}
      </h1>

      <div class="PageOne__Main position-relative flex-grow-1">

        <!-- DEEL 1 -->
        <div class="PageOne__Bar">
          <div class="Bar__Icon">
            <img width="25" :src="image({ name: 'factsheet_auto.png' })" />
          </div>
          <div class="Bar__Title">
            Prognose elektrische auto's*
          </div>
          <div class="Bar__End">
            bewoners, forenzen &amp; bezoekers
          </div>
        </div>

        <div class="Main__Content d-flex justify-content-between">
          <div class="Circle pt-3 pb-3" style="width: 120px">
            <div class="Circle">
              <span>{{ registeredVehicles }}</span>
            </div>
            <div># geregistreerde</div>
            <div>personenauto's</div>
          </div>
          <div class="BarChart mb-1">
            <BarChart v-if="display === 'basic'" :data="barChart" />
            <DetailedBarChart v-else :data="barChartDetailed" />
          </div>
          <div class="Text flex-shrink-1 pt-3" style="width: 278px">
            <div>Verwachte aantal elektrische auto's die in</div>
            <div>uw gemeenten een (semi)publieke of private</div>
            <div>laadbehoefte kennen </div>
            <div>(bewoners, forenzen &amp; bezoekers)</div>
          </div>
        </div>


        <!-- DEEL 2 -->

        <div class="PageOne__Bar">
          <div class="Bar__Icon">
            <img width="25" :src="image({ name: 'factsheet_laadpaal.png' })" />
          </div>
          <div class="Bar__Title">
            Toekomstige behoefte (semi) publieke laadpalen*
          </div>
        </div>

        <div class="Main__Content d-flex justify-content-between">
          <div class="Circle pt-5" style="width: 120px">
            <div class="Circle">
              <img width="25" :src="image({ name: 'factsheet_laadpaal.png' })" />
            </div>
            <div class="pt-3">
              <strong>Huidig aantal laadpalen**</strong>
            </div>
            <div>{{ currentPublicChargers }} publiek</div>
            <div>{{ currentSemiPublicChargers }} semi-publiek</div>
          </div>

          <div class="BarChart mb-1">
            <ColumnChart v-if="display === 'basic'" :data="columnChart" />
            <DetailedColumnChart v-else :data="columnChartDetailed" />
          </div>

          <div class="Text flex-shrink-1 pt-3" style="width: 278px">
            <div>Om uit te komen op de totale laadbehoefte is</div>
            <div class="pb-3">uitgegaan van deelgebruik per <i>laadpaal</i>:</div>

            <table>
              <tr>
                <td></td>
                <td><strong>2022</strong></td>
                <td><strong>2025</strong></td>
                <td><strong>2030</strong></td>
              </tr>
              <tr>
                <td><i>Bewoners</i></td>
                <td>1 op 5</td>
                <td>1 op 8</td>
                <td>1 op 8</td>
              </tr>
              <tr>
                <td><i>Forenzen</i></td>
                <td>1 op 4</td>
                <td>1 op 8</td>
                <td>1 op 8</td>
              </tr>
              <tr>
                <td><i>Bezoekers</i></td>
                <td>1 op 20</td>
                <td>1 op 40</td>
                <td>1 op 40</td>
              </tr>
            </table>
          </div>
        </div>
        
        <!-- DEEL 3 -->

        <div class="PageOne__Bar">
          <div class="Bar__Icon">
            <img width="25" :src="image({ name: 'factsheet_parking.png' })" />
          </div>
          <div class="Bar__Title">
            Verdeling openbaar en privaat parkeren***
          </div>
        </div>

        <div class="Main__Content d-flex justify-content-between">
          <div class="pt-3 d-flex justify-content-center flex-column">
            <div>Parkeren in de</div>
            <div>openbare ruimte</div>
          </div>
          <div class="pt-3 d-flex justify-content-center flex-column">
            <div style="width: 14px; height: 14px; background: #3B547B"></div>
            <div class="pt-1">{{ donutChart[0][2] }}</div>
          </div>
          <DonutChart :data="donutChart" />
          <div class="pt-3 d-flex justify-content-center flex-column">
            <div style="width: 15px; height: 15px; background: #689AC5"></div>
            <div class="pt-1">{{ donutChart[1][2] }}</div>
          </div>
          <div class="pt-3 d-flex justify-content-center flex-column">
            <div>Eigen</div>
            <div>parkeer- </div>
            <div>voorziening</div>
          </div>
          <div class="Text flex-shrink-1 pt-3" style="width: 278px">
            <div>Dubbelgebruik op (semi)publieke laadpalen</div>
            <div>betekent dat deze verhouding zich niet</div>
            <div>direct vertaalt in de aantallen laadpalen </div>
            <div>hierboven</div>
          </div>
        </div>


      </div>

      <div class="PageOne__Footer mt-2 d-flex justify-content-between">
        <img class="Footer__Logo" :src="image({ name: 'evc-circle.png' })" />

        <div class="Footer__Text font-italic">
          <div>
            <div>* Op basis van NAL prognose van 1.9 miljoen EVs in 2030 (bron: RVO, NAL-prognoses &amp; SparkCity model van de TU Eindhoven en EVTools)</div>
            <div>** Huidig aantal laadpalen o.b.v. meest recente data Klimaatmonitor; (semi)publieke laadpalen.</div>
            <div>*** Op basis van EVTools parkeerareaal-analyse m.b.v. open databronnen zoals BGT en eigen algoritmes</div>
          </div>
        </div>

        <img class="Footer__Background" :src="image({ name: 'factsheet_footer_background.png' })" />
      </div>
    </Page>
    <Page v-else :landscape="true">
      <h1 class="text-center pt-5">
        Er is voor {{ municipalityLabel }} nog geen factsheet beschikbaar
      </h1>
    </Page>
  </div>
</template>

<script>
import { image } from '@/helpers/assets'

import InlineToggle from '@/components/common/InlineToggle'
import Page from '@/components/factsheet/Page'
import BarChart from '@/components/factsheet/BarChart'
import DetailedBarChart from '@/components/factsheet/DetailedBarChart'
import ColumnChart from '@/components/factsheet/ColumnChart'
import DetailedColumnChart from '@/components/factsheet/DetailedColumnChart'
import DonutChart from '@/components/factsheet/DonutChart'

import { numberWithDots } from '@/helpers/number'

import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'

import { labelByCode } from '@/services/municipalities'
import { mapGetters } from 'vuex'

let factsheetData = require('@/data/factsheet.json')

export default {
  components: { InlineToggle, Page, BarChart, ColumnChart, DonutChart, DetailedBarChart, DetailedColumnChart },
  data() {
    return { 
      /**
       * The displayed factsheet layout. Either basic or detailed
       */
      display: 'basic'
    }
  },
  computed: {
    ...mapGetters('access', [
      'getActiveMunicipality'
    ]),
    municipalityLabel() {
      return labelByCode({ code: this.getActiveMunicipality })
    },
    hasFactsheetData() {
      return !!factsheetData[this.getActiveMunicipality]
    },
    factsheetData() {
      return factsheetData[this.getActiveMunicipality]
    },
    registeredVehicles() {
      return numberWithDots(this.factsheetData.registeredVehicles)
    },
    currentPublicChargers() {
      return this.factsheetData.currentChargers ? Math.ceil(this.factsheetData.currentChargers.public) : 2000
    },
    currentSemiPublicChargers() {
      return this.factsheetData.currentChargers ? Math.ceil(this.factsheetData.currentChargers.company) : 8000
    },
    /**
     * # Of EV's per year (public & private combined)
     */
    barChart() {
      return this.factsheetData.ev
    },
    barChartDetailed() {
      return this.factsheetData.evSplit
    },
    columnChart() {
      return this.factsheetData.chargers
    },
    columnChartDetailed() {
      return this.factsheetData.chargersSplit
    },
    donutChart() {
      let priv = this.factsheetData.parking.private
      let pub = this.factsheetData.parking.public

      return [ // % Parkingspace
        ['parkeren in de openbare ruimte', (pub/2), `${Math.round(pub)}%`],
        ['eigen perkaarvoorziening', (priv/2), `${100 - Math.round(pub)}%`],
        [null, 50]
      ] 
    }
  },
  methods: {
    image,
    /**
     * Handle the display selection
     */
    handleSelected({ selected }) {
      this.display = selected
    },
    /**
     * Download the Factsheet as PDF
     */
    handlePrint() {
      html2canvas(this.$refs.factsheet.$el).then(canvas => {
        let context = canvas.getContext('2d')
        context['imageSmoothingEnabled'] = false; /* standard */
        context['mozImageSmoothingEnabled'] = false; /* Firefox */
        context['oImageSmoothingEnabled'] = false; /* Opera */
        context['webkitImageSmoothingEnabled'] = false; /* Safari */
        context['msImageSmoothingEnabled'] = false; /* IE */

        const doc = new jsPDF({
          orientation: 'landscape'
        })

        console.log(doc.internal.pageSize.getWidth(), doc.internal.pageSize.getHeight())

        doc.addImage(
          canvas, 
          'JPEG', 
          0, 
          0, 
          Math.floor(doc.internal.pageSize.getWidth()),  
          Math.floor(doc.internal.pageSize.getHeight())
        )
        let layout = this.display === 'basic' ? ' ' : '(detail) '
        doc.save(`Factsheet ${layout}- ${ this.municipalityLabel } - EV Consult.pdf`)
      })
    }
  }
}
</script>

<style lang="scss">

/**
 * Measurements are taken from a powerpoint "design", which has a different aspect ratio.
 * The factors below are used to help convert pp measurements into pixels
 */
$height: 1.96;
$width: 1.56;
$aspect: (($width + $height) /2);

.PrintBtn {
  // position: absolute;
  top: 1rem;
  right: 1rem;
}

.Pages {
  position: relative;
  overflow-y: scroll;

  &__Controls {
    width: 29.7cm;
    margin: auto;
  }
}

.PageOne {
  &__Header {
    text-align: center;
    margin-top: 14px * $height;
    line-height: 41px * $height;
    font-size: 28px * $aspect;
    color: #3B547B;
  }
  &__Main {
    width: 600px * $width;
    margin: 0 auto;
    color: #7F7F7F;

    .Main {
      &__Content {
        padding: 0 7px * $width;
      }
    }

    table {
      width: 100%;

      td {
        padding: 6px 0
      }
    }

    .Circle {
      text-align: center;

      .Circle {
        background: #689AC5;
        width: 60px;
        height: 60px;
        border-radius: 50%;
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 13px;
        margin: 0 auto 3px;
      }

    }
  }
  &__Bar {
    height: 22px * $height;
    background: #3B547B;
    color: white;
    padding: 0 10px;

    display: flex;
    align-items: center;

    .Bar__Icon {
      width: 35px * $width;
      margin-left: 4px * $width;
      display: flex;
      align-items: center;
      font-size: 14px * $aspect;

      .SvgIcon {
        fill: white;
      }
    }
    .Bar__Title {
      flex-grow: 1;
    }
    .Bar__Title, .Bar__End {
      margin: 0;
      font-size: 11px * $aspect;
    }
  }
  &__Footer {
    position: relative;

    .Footer {

      &__Logo {
        position: absolute;
        left: 28px * $width;
        bottom: 16px * $height;
        width: 34px * $aspect; // Aspect ratio...
        height: 31px * $aspect; // Aspect ratio...
      }

      &__Background {
        position: absolute;
        right: 0;
        bottom: 0;
        width: 357px; // * $aspect; // Aspect ratio... 
        height: 74px; // * $aspect; // Aspect ratio...
      }

      &__Text {
        position: absolute;
        z-index: 99999;
        bottom: 8px * $height;
        width: 100%;
        height: 25px * $height;
        font-size: 7px * $aspect;

        > div {
          width: 526px * $width;
          margin: 0 auto;
        }
      }

    }
  }
}
</style>